import React, { PropsWithChildren } from 'react';
import ReactHelmet from 'react-helmet';
import Mustache from 'mustache';
import { useLocation } from 'react-router';
import { useSelector } from 'src/client/redux';
import { GenericObject } from 'src/@types/type-utils';
import { logoUrl } from 'src/client/helpers/urls/logoUrl';

type Props = PropsWithChildren<{
  id: string | string[];
  canonicalUrl?: string;
  defaultTitle?: string;
  defaultDescription?: string;
  forceDefaultTitle?: boolean;
  forceDefaultDescription?: boolean;
  showSchema?: boolean;
  variables: GenericObject;
}>;

export default function Helmet(props: Props) {
  const {
    canonicalUrl,
    children,
    defaultDescription,
    defaultTitle,
    forceDefaultDescription,
    forceDefaultTitle,
    id,
    showSchema = false,
    variables,
  } = props;

  const { cdnUrl, config, siteSubdomain } = useSelector((state) => state.app);
  const site = useSelector((state) => state.site);
  const { metaTags } = config;
  const location = useLocation();

  const ids = typeof id === 'string' ? [id] : id;

  const metaTagIndex = metaTags && ids.find((id) => id in metaTags);
  const metaTag = metaTagIndex ? metaTags[metaTagIndex] : undefined;

  let metaTitle = defaultTitle;
  if (!forceDefaultTitle && metaTag?.title !== undefined) {
    metaTitle = metaTag.title;
  }
  let metaDescription = defaultDescription;
  if (!forceDefaultDescription && metaTag?.description !== undefined) {
    metaDescription = metaTag.description;
  }

  if (metaTitle) {
    try {
      metaTitle = Mustache.render(metaTitle, variables);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Cant parse title template');
    }
  }

  if (metaDescription) {
    try {
      metaDescription = Mustache.render(metaDescription, variables);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Cant parse description template');
    }
  }
  const url = canonicalUrl ?? `${window.location.origin}${location.pathname}${location.search}${location.hash}`;

  const schemaMarkupOrg: Record<string, unknown> = {
    '@context': 'https://schema.org/',
    '@type': 'Organization',
    url: `https://${siteSubdomain}.${site.domainCanonical}/`,
    logo: `${logoUrl(cdnUrl, site)}`,
    image: `${logoUrl(cdnUrl, site)}`,
    name: `${site.name}`,
  };

  const sameAsLinks: string[] = [];

  if (config.reddit) {
    sameAsLinks.push(`https://www.reddit.com/r/${config.reddit}`);
  }
  if (config.instagram) {
    sameAsLinks.push(`https://www.instagram.com/${config.instagram}`);
  }
  if (config.twitter) {
    sameAsLinks.push(`https://twitter.com/${config.twitter}`);
  }

  if (sameAsLinks.length > 0) {
    schemaMarkupOrg.sameAs = sameAsLinks;
  }

  const schemaMarkupWebsite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: `https://${siteSubdomain}.${site.domainCanonical}/`,
    name: `${site.name}`,
  };

  const schemaMarkupCreativeWorkSeries = {
    '@context': 'https://schema.org/',
    '@type': 'CreativeWorkSeries',
    name: `${variables.modelName} Nude`,
    text: metaDescription,
  };

  const schemaMarkupSearchAction = {
    '@context': 'https://schema.org/',
    '@type': 'WebSite',
    url: `https://${site.domainCanonical}`,
    potentialAction: {
      '@type': 'SearchAction',
      target: `https://${site.domainCanonical}/search/{search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  // You can't use <> fragments inside react helmet.
  return (
    <ReactHelmet>
      {metaTitle && <title>{metaTitle}</title>}
      {metaTitle && <meta property="og:title" content={metaTitle} />}
      {metaDescription && <meta property="og:description" content={metaDescription} />}
      {metaDescription && <meta name="description" content={metaDescription} />}
      {showSchema && <script type="application/ld+json">{JSON.stringify(schemaMarkupOrg)}</script>}
      {showSchema && <script type="application/ld+json">{JSON.stringify(schemaMarkupWebsite)}</script>}
      {ids.includes('model') && (
        <script type="application/ld+json">{JSON.stringify(schemaMarkupCreativeWorkSeries)}</script>
      )}
      <script type="application/ld+json">{JSON.stringify(schemaMarkupSearchAction)}</script>
      {children}
      <meta property="og:url" content={url} />
      <link rel="canonical" href={url} />
    </ReactHelmet>
  );
}
